import { useMemo, useState } from 'react';
import { GitMerge } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SectionLabel } from '@/components/ui/section-label';
import {
  BAND_PLURAL,
  MAX_MEMBERS_PER_CLUSTER,
  excludedInCluster,
  excludedKey,
  groupIntoBands,
  includedMembers,
  mergeUnion,
  placeAssignments,
  planMerge,
  type AnchoredTheme,
  type BandType,
  type MergeCandidate,
} from '@/lib/synthesis-board';
import { toast } from '@/hooks/use-toast';
import {
  usePromoteCluster,
  useDismissCluster,
  useAcceptAssignment,
  useDismissAssignment,
} from '@/hooks/use-synthesis';
import type {
  PromoteClusterInput,
  StreamedCluster,
  SynthesisAssignment,
  SynthesisCluster,
} from '@/types/api';
import { ClusterGroup } from './ClusterGroup';
import { OrphanAssignments } from './OrphanAssignments';

export type AnchoredThemeRow = AnchoredTheme & {
  respondentCount?: number;
  respondentTotal?: number;
  memberIds: string[];
};

// Owns everything that spans containers: which clusters are marked for merge,
// which members are unticked, and the title edits. Held here rather than per
// container so a merge can read the exclusions of every cluster it unions.
// Exclusions are keyed by cluster *and* item (see excludedKey): the same finding
// can sit in more than one theme, and unticking it in one must not untick it in
// the others.
export function AffinityBoard({
  projectId,
  clusters,
  respondentTotal,
  anchored,
  assignments,
  labelOf,
  typeOf,
  canEdit,
  streaming,
  streamed,
  canvasReady,
}: {
  projectId: string;
  clusters: SynthesisCluster[];
  respondentTotal: number;
  anchored: AnchoredThemeRow[];
  assignments: SynthesisAssignment[];
  labelOf: (id: string) => string;
  typeOf: (id: string) => BandType | undefined;
  canEdit: boolean;
  streaming?: boolean;
  streamed?: StreamedCluster[];
  // Whether the canvas query (anchored themes + typeOf) has actually loaded,
  // as opposed to still being in flight or having failed. Only gates the
  // orphan strip below, not the rest of the board: proposed clusters render
  // correctly from the proposal alone.
  canvasReady: boolean;
}) {
  const promote = usePromoteCluster(projectId);
  const dismiss = useDismissCluster(projectId);
  const busy = promote.isPending || dismiss.isPending;

  const acceptAssignment = useAcceptAssignment(projectId);
  const dismissAssignment = useDismissAssignment(projectId);
  const assignBusy = acceptAssignment.isPending || dismissAssignment.isPending;

  const placement = useMemo(
    () => placeAssignments(assignments, anchored, typeOf),
    [assignments, anchored, typeOf],
  );

  // Breadth ordering within a band, same rationale as sortClusters for proposed
  // themes: how many respondents raised it is the signal, not graph order.
  // AnchoredThemeRow has no representativeType/itemCount, so sortClusters (built
  // for RankableCluster) doesn't fit here — a plain comparator does the job.
  const anchoredByType = useMemo(() => {
    const m = new Map<BandType, AnchoredThemeRow[]>();
    for (const t of anchored) {
      const bucket = m.get(t.type) ?? [];
      bucket.push(t);
      m.set(t.type, bucket);
    }
    for (const bucket of m.values()) {
      bucket.sort((a, b) => (b.respondentCount ?? 0) - (a.respondentCount ?? 0));
    }
    return m;
  }, [anchored]);

  const rejectAssignment = (a: SynthesisAssignment) =>
    dismissAssignment.mutateAsync(a.id).catch((err) =>
      toast({
        title: 'Dismiss failed',
        description: err instanceof Error ? err.message : '',
        variant: 'destructive',
      }),
    );

  const [merging, setMerging] = useState<Set<string>>(new Set());
  // Composite `excludedKey(clusterId, itemId)` entries, never bare item ids.
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [titles, setTitles] = useState<Record<string, string>>({});

  const titleOf = (c: SynthesisCluster) => titles[c.id] ?? c.title;
  const bands = useMemo(() => groupIntoBands(clusters), [clusters]);

  const toggleIn = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  // Remove-only, deliberately not toggleIn: both call sites below clear a mark
  // that a completed action has made obsolete. Absence is the normal state for
  // a cluster (unmarked), so toggling here would re-mark one that was never
  // marked, and once the cluster is gone from the list that stray mark can
  // never be cleared again.
  const clearMark = (set: Set<string>, id: string) => {
    const next = new Set(set);
    next.delete(id);
    return next;
  };

  // Filter the rendered bands, not the raw clusters prop: planMerge promotes
  // chosen[0] as the surviving theme, so the merge head must be whichever
  // marked card the user actually sees first. clusters is ordered by
  // respondent count alone, while bands (and the DOM) break ties by item
  // count then title, so filtering clusters directly can silently pick a
  // different head than the one on screen and drop that head's title edit.
  // Merges are same-type only, so flattening across bands is safe and just
  // guarantees this matches render order.
  const chosen = bands.flatMap((b) => b.clusters).filter((c) => merging.has(c.id));
  const candidates: MergeCandidate[] = chosen.map((c) => ({
    id: c.id,
    title: titleOf(c),
    originalTitle: c.title,
    description: c.description,
    representativeType: c.representativeType,
    memberItemIds: c.memberItemIds,
  }));
  // planMerge is the single source of truth for whether a merge may run; the
  // banner below still needs the individual reasons to explain *why* not, and
  // the merge button below derives its enabled state from plan directly so it
  // can never drift from planMerge's rules.
  const plan = planMerge(candidates, excluded);
  const union = mergeUnion(chosen, excluded);
  const mixedTypes = new Set(chosen.map((c) => c.representativeType)).size > 1;

  const doPromote = (payload: PromoteClusterInput, type: string) =>
    promote
      .mutateAsync(payload)
      .then(() => {
        setMerging((prev) => clearMark(prev, payload.clusterId));
        toast({ title: `Promoted to a synthesised ${type}` });
      })
      .catch((err) =>
        toast({
          title: 'Promote failed',
          description: err instanceof Error ? err.message : '',
          variant: 'destructive',
        }),
      );

  // Every rule about whether a merge may run, and exactly what it writes, lives
  // in planMerge — a null plan means issue nothing. This component only performs
  // the plan, so the destructive path is covered by planMerge's unit tests
  // rather than by a copy of its logic.
  const doMerge = async () => {
    if (!plan) return;
    const type = chosen[0].representativeType;
    const count = chosen.length;
    // The promote is the half that writes to the graph. Once it lands, a failed
    // dismiss leaves a real synthesised node behind, so reporting "Merge
    // failed" would be untrue and would invite a retry that promotes a second
    // copy. Clearing the marks either way keeps the banner from offering a
    // merge over a theme that is already promoted.
    let promoted = false;
    try {
      await promote.mutateAsync(plan.promote);
      promoted = true;
      for (const id of plan.dismissIds) await dismiss.mutateAsync(id);
      setMerging(new Set());
      toast({ title: `Merged ${count} themes into one ${type}` });
    } catch (err) {
      if (promoted) setMerging(new Set());
      toast({
        title: promoted
          ? `Merged into one ${type}, but some themes stayed on the board`
          : 'Merge failed',
        description: err instanceof Error ? err.message : '',
        variant: 'destructive',
      });
    }
  };

  // Previews are the model's raw output: the server normalises before persisting
  // and may drop some, so these are render-only and carry no actions. They have
  // no respondentCount yet, so no meter is drawn for them.
  const streamedByType = useMemo(() => {
    const m = new Map<BandType, StreamedCluster[]>();
    for (const c of streamed ?? []) {
      const type = c.representativeType;
      if (!c.title || (type !== 'job' && type !== 'challenge' && type !== 'opportunity')) continue;
      const bucket = m.get(type) ?? [];
      bucket.push(c);
      m.set(type, bucket);
    }
    return m;
  }, [streamed]);

  return (
    <div className="space-y-5">
      {chosen.length >= 2 && (
        <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/40 px-3 py-2 text-sm">
          <span>
            {chosen.length} themes selected
            {mixedTypes && (
              <span className="text-muted-foreground">
                {' '}
                · pick a single type to merge, since jobs, challenges and opportunities can&rsquo;t combine
              </span>
            )}
            {!mixedTypes && union.overCap && (
              <span className="text-muted-foreground">
                {` · that would attach ${union.count} findings to one theme, and the limit is ${MAX_MEMBERS_PER_CLUSTER}. Untick a few first.`}
              </span>
            )}
            {!mixedTypes && !union.overCap && union.count === 0 && (
              <span className="text-muted-foreground">
                {' '}
                · every finding is unticked, and a theme needs at least one. Tick one back
                first.
              </span>
            )}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setMerging(new Set())}>
              Clear
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={doMerge}
              disabled={!canEdit || busy || !plan}
            >
              <GitMerge className="mr-1.5 h-3.5 w-3.5" />
              Merge and promote
            </Button>
          </div>
        </div>
      )}

      {bands.map((band) => (
        <div key={band.type}>
          <SectionLabel className="flex items-center gap-2 pb-2">
            <span>{BAND_PLURAL[band.type]}</span>
            <span className="h-px flex-1 bg-border" />
            <span className="tabular-nums text-muted-foreground">{band.clusters.length || ''}</span>
          </SectionLabel>

          {(streamedByType.get(band.type) ?? []).length > 0 && (
            <div className="mb-2 grid gap-2 md:grid-cols-2">
              {(streamedByType.get(band.type) ?? []).map((c, i) => (
                <div
                  key={`streamed-${band.type}-${i}`}
                  className="animate-in fade-in rounded-lg border border-dashed bg-muted/30 p-2.5 opacity-80"
                >
                  <span className="text-sm font-medium leading-snug">{c.title}</span>
                  <span className="ml-2 text-[11px] tabular-nums text-muted-foreground">
                    {c.memberItemIds?.length ?? 0} finding{(c.memberItemIds?.length ?? 0) === 1 ? '' : 's'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {band.clusters.length === 0 ? (
            // Silent while a run is in flight (asserting "none found" over a
            // band that is still filling would be false), silent when the whole
            // proposal is empty, where the view says it once instead of three
            // times, and silent over a band that already has promoted themes
            // below it — the line is about the run, the containers under it are
            // about the graph, so together they read as a contradiction. The
            // line's real job is the mixed case: a finished run that returned
            // something for one type and nothing for another, where an absent
            // band would read as a truncation.
            streaming ||
            clusters.length === 0 ||
            (anchoredByType.get(band.type) ?? []).length > 0 ? null : (
              <p className="text-xs text-muted-foreground">The latest run found none of these.</p>
            )
          ) : (
            <div className="grid gap-2 md:grid-cols-2">
              {band.clusters.map((c) => (
                <ClusterGroup
                  key={c.id}
                  variant="proposed"
                  type={c.representativeType}
                  title={titleOf(c)}
                  description={c.description}
                  respondentCount={c.respondentCount}
                  respondentTotal={respondentTotal || undefined}
                  itemCount={c.itemCount}
                  memberIds={c.memberItemIds}
                  labelOf={labelOf}
                  excluded={excludedInCluster(c.id, c.memberItemIds, excluded)}
                  included={includedMembers(c.id, c.memberItemIds, excluded)}
                  onToggleMember={(id) => setExcluded((prev) => toggleIn(prev, excludedKey(c.id, id)))}
                  onTitleChange={(t) => setTitles((prev) => ({ ...prev, [c.id]: t }))}
                  onPromote={(memberItemIds) =>
                    void doPromote(
                      {
                        clusterId: c.id,
                        title: titleOf(c).trim() || c.title,
                        description: c.description,
                        memberItemIds,
                      },
                      c.representativeType,
                    )
                  }
                  onReject={() =>
                    void dismiss
                      .mutateAsync(c.id)
                      .then(() => setMerging((prev) => clearMark(prev, c.id)))
                      .catch((err) =>
                        toast({
                          title: 'Reject failed',
                          description: err instanceof Error ? err.message : '',
                          variant: 'destructive',
                        }),
                      )
                  }
                  merging={merging.has(c.id)}
                  onToggleMerge={() => setMerging((prev) => toggleIn(prev, c.id))}
                  canEdit={canEdit}
                  busy={busy}
                />
              ))}
            </div>
          )}

          {(anchoredByType.get(band.type) ?? []).length > 0 && (
            <div className="mt-2 grid gap-2 md:grid-cols-2">
              {(anchoredByType.get(band.type) ?? []).map((t) => (
                <ClusterGroup
                  key={t.id}
                  variant="anchored"
                  type={t.type}
                  title={t.title}
                  respondentCount={t.respondentCount ?? 0}
                  respondentTotal={t.respondentTotal}
                  itemCount={t.memberIds.length}
                  memberIds={t.memberIds}
                  labelOf={labelOf}
                  excluded={new Set()}
                  included={t.memberIds}
                  onToggleMember={() => {}}
                  pending={placement.byThemeId.get(t.id) ?? []}
                  onAcceptAssignment={(a) =>
                    void acceptAssignment
                      .mutateAsync(a.id)
                      .then(() => toast({ title: 'Added to existing theme' }))
                      .catch((err) =>
                        toast({
                          title: 'Accept failed',
                          description: err instanceof Error ? err.message : '',
                          variant: 'destructive',
                        }),
                      )
                  }
                  onRejectAssignment={(a) => void rejectAssignment(a)}
                  canEdit={canEdit}
                  busy={assignBusy}
                />
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Gate on canvasReady, not just placement.orphans: while the canvas
          query is loading or has failed, anchored is [] and typeOf resolves
          nothing, so placeAssignments would misclassify every pending
          assignment as an orphan. Rendering that strip would offer reject
          (an irreversible status change) on a suggestion we have no real
          basis to call unplaceable. Do not remove this as "redundant" with
          the orphans-empty check inside OrphanAssignments; it is a different
          condition (no data, not no orphans). */}
      {canvasReady && (
        <OrphanAssignments
          orphans={placement.orphans}
          labelOf={labelOf}
          onReject={(a) => void rejectAssignment(a)}
          canEdit={canEdit}
          busy={assignBusy}
        />
      )}
    </div>
  );
}
